import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Camera, RefreshCw, Upload, FileText, Sun, Moon, HardDrive, CheckCircle, Clock, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyShootsPage = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');

  // Card Numbers dialog
  const [showCardDialog, setShowCardDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [cardCount, setCardCount] = useState(1);

  // Upload Card Data dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    card_id: '', first_clip: '', last_clip: '', total_clips: '', nas_clips: '',
    card_data_gb: '', nas_data_gb: '', notes: ''
  });
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [firstImageFile, setFirstImageFile] = useState(null);
  const [lastImageFile, setLastImageFile] = useState(null);

  useEffect(() => { fetchMyEvents(); }, []);

  const fetchMyEvents = async () => {
    try {
      const res = await api.get('/vault/my-events');
      setEvents(res.data);
    } catch (err) {
      toast.error('Failed to load shoots');
    } finally {
      setLoading(false);
    }
  };

  const pendingEvents = events.filter(e => e.status === 'pending');
  const completedEvents = events.filter(e => e.status !== 'pending');

  const openCardDialog = (ev) => {
    setSelectedEvent(ev);
    setCardCount(ev.cards_total || 1);
    setShowCardDialog(true);
  };

  const handleSetCards = async () => {
    try {
      await api.patch(`/vault/projects/${selectedEvent.project_id}/events/${selectedEvent.event_id}/cards`, {
        user_id: user.id,
        cards_total: cardCount,
        cards_uploaded: selectedEvent.cards_uploaded || 0,
      });
      toast.success('Card numbers set');
      setShowCardDialog(false);
      fetchMyEvents();
    } catch (err) {
      toast.error('Failed to set cards');
    }
  };

  const openUploadDialog = (ev) => {
    setSelectedEvent(ev);
    setUploadForm({ card_id: '', first_clip: '', last_clip: '', total_clips: '', nas_clips: '', card_data_gb: '', nas_data_gb: '', notes: '' });
    setScreenshotFile(null);
    setFirstImageFile(null);
    setLastImageFile(null);
    setShowUploadDialog(true);
  };

  const handleUploadCard = async () => {
    if (!uploadForm.card_id || !uploadForm.card_data_gb) {
      toast.error('Card ID and Card Data (GB) are required');
      return;
    }
    try {
      // Upload screenshot if provided
      let screenshotUrl = null;
      if (screenshotFile) {
        const formData = new FormData();
        formData.append('file', screenshotFile);
        const uploadRes = await api.post(`/vault/projects/${selectedEvent.project_id}/events/${selectedEvent.event_id}/screenshots`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        screenshotUrl = uploadRes.data.url;
      }

      // Update cards_uploaded count
      const newUploaded = (selectedEvent.cards_uploaded || 0) + 1;
      await api.patch(`/vault/projects/${selectedEvent.project_id}/events/${selectedEvent.event_id}/cards`, {
        user_id: user.id,
        cards_total: selectedEvent.cards_total || 1,
        cards_uploaded: newUploaded,
      });

      toast.success(`Card ${uploadForm.card_id} data saved`);
      setShowUploadDialog(false);
      fetchMyEvents();
    } catch (err) {
      toast.error('Failed to upload card data');
    }
  };

  const displayEvents = activeTab === 'pending' ? pendingEvents : completedEvents;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-shoots-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">My Shoots</h1>
          <p className="text-zinc-500 text-sm mt-1">Your assigned events & data entry</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchMyEvents}
          className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10" data-testid="refresh-shoots">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10">
        <button onClick={() => setActiveTab('pending')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'pending' ? 'text-[#F5A623] border-[#F5A623]' : 'text-zinc-500 border-transparent hover:text-white'
          }`} data-testid="tab-pending">
          Pending ({pendingEvents.length})
        </button>
        <button onClick={() => setActiveTab('completed')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'completed' ? 'text-emerald-400 border-emerald-400' : 'text-zinc-500 border-transparent hover:text-white'
          }`} data-testid="tab-completed">
          Completed ({completedEvents.length})
        </button>
      </div>

      {/* Events */}
      {displayEvents.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          <Camera className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>{activeTab === 'pending' ? 'No pending shoots' : 'No completed shoots yet'}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {displayEvents.map(ev => (
            <Card key={`${ev.project_id}-${ev.event_id}`} className="bg-[#121212] border-white/10" data-testid={`shoot-${ev.event_id}`}>
              <CardContent className="p-5">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-white font-semibold text-lg">{ev.client_name}</h3>
                    <p className="text-sm text-zinc-500">{ev.event_date}</p>
                  </div>
                  <Badge variant="outline" className={ev.status === 'pending'
                    ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                    : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                  }>
                    {ev.status === 'pending' ? <><Clock className="h-3 w-3 mr-1" /> Pending</> : <><CheckCircle className="h-3 w-3 mr-1" /> Complete</>}
                  </Badge>
                </div>

                {/* Event Name */}
                <div className="p-3 bg-[#0A0A0A] rounded-lg border border-white/5 mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Sun className="h-4 w-4 text-amber-400" />
                    <span className="text-white font-medium">{ev.event_name}</span>
                    <Badge variant="outline" className={`text-[10px] ${ev.brand === 'triobliss' ? 'bg-pink-500/10 text-pink-400 border-pink-500/20' : 'bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20'}`}>
                      {ev.brand === 'triobliss' ? 'Trio Bliss' : 'WS'}
                    </Badge>
                  </div>

                  {/* Card Status */}
                  {ev.cards_total > 0 ? (
                    <div className="mt-2">
                      <p className="text-xs text-zinc-400 mb-1">Expected Cards: <span className="text-white font-medium">{ev.cards_total}</span></p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-[#F5A623] rounded-full transition-all" style={{ width: `${Math.min(100, (ev.cards_uploaded / ev.cards_total) * 100)}%` }} />
                        </div>
                        <span className="text-xs text-zinc-400">{ev.cards_uploaded}/{ev.cards_total}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-center gap-1 text-amber-400 text-xs">
                      <AlertTriangle className="h-3 w-3" />
                      <span>Card numbers not set</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openCardDialog(ev)}
                    className="border-white/10 text-zinc-300 hover:bg-white/5 text-xs" data-testid={`set-cards-${ev.event_id}`}>
                    <FileText className="h-3 w-3 mr-1.5" /> Set Card Numbers
                  </Button>
                  {ev.cards_total > 0 && (
                    <Button size="sm" onClick={() => openUploadDialog(ev)}
                      className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black text-xs" data-testid={`upload-card-${ev.event_id}`}>
                      <Upload className="h-3 w-3 mr-1.5" /> Upload Card
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Set Card Numbers Dialog */}
      <Dialog open={showCardDialog} onOpenChange={setShowCardDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Set Card Numbers</DialogTitle>
            <DialogDescription className="text-zinc-400">{selectedEvent?.event_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs">Number of Cards</Label>
              <Input type="number" min="1" value={cardCount} onChange={e => setCardCount(parseInt(e.target.value) || 1)}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="card-count-input" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCardDialog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleSetCards} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="save-cards-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Card Data Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Card Data</DialogTitle>
            <DialogDescription className="text-zinc-400">{selectedEvent?.event_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">Card ID *</Label>
                <Input value={uploadForm.card_id} onChange={e => setUploadForm({...uploadForm, card_id: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="e.g., SD-01" data-testid="upload-card-id" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Card Screenshot</Label>
                <label className="flex items-center gap-2 px-3 py-2 mt-1 bg-[#0A0A0A] border border-white/10 rounded-md cursor-pointer hover:border-white/20 transition-colors">
                  <Upload className="h-3 w-3 text-zinc-400" />
                  <span className="text-xs text-zinc-400 truncate">{screenshotFile ? screenshotFile.name : 'Choose file'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={e => setScreenshotFile(e.target.files[0])} />
                </label>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">First Clip #</Label>
                <Input type="number" value={uploadForm.first_clip} onChange={e => setUploadForm({...uploadForm, first_clip: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="1" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Last Clip #</Label>
                <Input type="number" value={uploadForm.last_clip} onChange={e => setUploadForm({...uploadForm, last_clip: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="100" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Total Clips</Label>
                <Input type="number" value={uploadForm.total_clips} onChange={e => setUploadForm({...uploadForm, total_clips: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="100" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">NAS Clips</Label>
                <Input type="number" value={uploadForm.nas_clips} onChange={e => setUploadForm({...uploadForm, nas_clips: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="100" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">Card Data (GB) *</Label>
                <Input type="number" step="0.1" value={uploadForm.card_data_gb} onChange={e => setUploadForm({...uploadForm, card_data_gb: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="32.5" data-testid="upload-card-gb" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">NAS Data (GB)</Label>
                <Input type="number" step="0.1" value={uploadForm.nas_data_gb} onChange={e => setUploadForm({...uploadForm, nas_data_gb: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="32.5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">First Image (Optional)</Label>
                <label className="flex items-center gap-2 px-3 py-2 mt-1 bg-[#0A0A0A] border border-white/10 rounded-md cursor-pointer hover:border-white/20">
                  <Upload className="h-3 w-3 text-zinc-400" />
                  <span className="text-xs text-zinc-400 truncate">{firstImageFile ? firstImageFile.name : 'Choose file'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={e => setFirstImageFile(e.target.files[0])} />
                </label>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Last Image (Optional)</Label>
                <label className="flex items-center gap-2 px-3 py-2 mt-1 bg-[#0A0A0A] border border-white/10 rounded-md cursor-pointer hover:border-white/20">
                  <Upload className="h-3 w-3 text-zinc-400" />
                  <span className="text-xs text-zinc-400 truncate">{lastImageFile ? lastImageFile.name : 'Choose file'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={e => setLastImageFile(e.target.files[0])} />
                </label>
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Notes</Label>
              <Textarea value={uploadForm.notes} onChange={e => setUploadForm({...uploadForm, notes: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1" rows={2} placeholder="Any notes from the shoot..." />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowUploadDialog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleUploadCard} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="submit-upload-btn">Upload Card</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyShootsPage;
